import { IMapZoneDetailDTO, IUserLocationRecordDTO, MailAddressModel, MapZonesClient, UserLocationEmailsModel, UserLocationRecordDTO, UserLocationRequestDTO, UsersLocationsClient, UsersLocationsFeaturesModelDTO, UsersLocationsRequestModel } from 'api/swagger/ApiClient';
import { autoinject, BindingEngine, computedFrom, Container, newInstance, TaskQueue } from 'aurelia-framework';
import { Disposable } from 'framework/aurelia_tools/binding-helper';
import { tryExecute } from 'framework/error_handling/error-utils';
import { TaskCounter } from 'framework/script_utilities/task-executor';
import * as L from 'leaflet';
import { UsersLocationsFilter } from 'model/filters/users-locations-filter';
import { UserLocation, UsersEmail, UsersLocationsMapFilter, UsersLocationsQueryMemory } from 'model/users-locations';
import * as E from 'support/errors';
import { BATCH_SIZE } from 'support/settings';
import { Site } from 'model/business-keywords';

@autoinject
export class UsersLocationsController {
  public queryMemory: UsersLocationsQueryMemory;
  public mapFilter = { isSynchronized: false, areSitesDisplayed: false } as UsersLocationsMapFilter;
  private previousMapFilter = { isSynchronized: false, areSitesDisplayed: false } as UsersLocationsMapFilter;
  public usersLocationsMap: UserLocation[] = [];
  public usersLocationsList: UserLocation[] = [];
  public showUserLocationDetailInMap: (userLocation: UserLocation) => void;
  public showUserLocationDetailInList: (userLocation: UserLocation) => void;
  public zoomUserLocationInMap: (userLocation: UserLocation) => void;
  public getUsersLocationsInView: () => UserLocation[];
  public getUsersLocationsOutsideView: () => UserLocation[];
  public changeSitesVisibility: (areDisplayed: boolean) => void;
  public showSiteOrLocationInMap: (site: Site, selectedLocation: L.LatLng, distanceInMeters: number) => void;
  public showNewSearchInMap: () => Promise<void>;
  public toggleLocationSelectingInMap: (isSelecting: boolean) => void;
  public isSelectedAny = false;
  public isSelectedAll = false;
  public loaded = false;
  public mapCenter: L.LatLng = null;
  public mapZoom: number = null;
  public features: UsersLocationsFeaturesModelDTO = null;
  public setSplitter: () => void;
  public storeSplitter: () => void;
  public isLocationSelecting = false;

  private lastQueryId = 0;
  private searchUniqueId: string;
  private searchTotalRowCount: number;
  private lastDetailUserLocation: UserLocation = null;
  private sortExpressionSubscription: Disposable = null;
  private currentSite: Site = null;
  private currentSelectedLocation: L.LatLng = null;
  private currentDistanceFromSite: number = null;

  constructor(container: Container, private bindingEngine: BindingEngine, private api: UsersLocationsClient, private mapZoneApi: MapZonesClient, @newInstance() public taskCounter: TaskCounter, private taskQueue: TaskQueue) {
    this.queryMemory = new UsersLocationsQueryMemory();
    this.queryMemory.initialize(UsersLocationsFilter);
    container.registerInstance(UsersLocationsQueryMemory, this.queryMemory);
  }

  /* Lifecycle methods */

  public async attach() {
    this.sortExpressionSubscription = this.bindingEngine.propertyObserver(this.queryMemory, 'sortExpression').subscribe(() => this.sortExpressionChanged());
    this.features = await this.api.getFeatures();
  }

  public detach() {
    this.sortExpressionSubscription.dispose();
  }

  /* Data loading methods */

  public async search() {
    await this.taskCounter.execute(() => tryExecute(() => this.newSearch(), E.FILTER_ERROR_MESSAGE, E.FILTER_ERROR_TITLE, true));
  }

  public async searchBySite(site: Site) {
    this.queryMemory.filter.country = null;
    this.queryMemory.filter.locationCurrentnessStatus = null;
    this.queryMemory.filter.mapZone = null;
    this.queryMemory.filter.site = site;
    this.queryMemory.filter.text = null;
    this.queryMemory.filter.user = null;
    await this.search();
  }

  private async newSearch() {
    const queryId = ++this.lastQueryId;
    const dataResult = await this.api.getNewUsersLatestLocationsSearch(0, this.batchSize, this.queryMemory.sortExpression, this.queryMemory.filter.user?.uniqueid, this.queryMemory.filter.user ? null : this.queryMemory.filter.text, this.queryMemory.filter.country?.id, this.queryMemory.filter.mapZone?.id, this.queryMemory.filter.mapZone != null ? this.queryMemory.filter.insideMapZone : null, this.queryMemory.filter.site != null ? this.queryMemory.filter.site.id : null, this.queryMemory.filter.selectedLocation != null ? this.queryMemory.filter.selectedLocation.lat : undefined, this.queryMemory.filter.selectedLocation != null ? this.queryMemory.filter.selectedLocation.lng : undefined, this.queryMemory.filter.site != null || this.queryMemory.filter.selectedLocation != null ? this.queryMemory.filter.distanceFromSiteInMeters : null, this.queryMemory.filter.locationCurrentnessStatus);
    if (queryId === this.lastQueryId) {
      this.clearLastDetailUserLocation();
      this.isSelectedAll = false;
      this.isSelectedAny = false;
      //invalidate previous search if exists, do not wait for the result
      if (this.searchUniqueId != null)
        this.api.invalidateQueryCache(this.searchUniqueId);
      this.searchUniqueId = dataResult.searchUniqueId;
      this.searchTotalRowCount = dataResult.usersLocationsForList.metaData.totalRowCount;
      this.usersLocationsMap = dataResult.usersLocationsForMap.map(x => this.wrapRecord(x));
      await this.showNewSearchInMap();
      if (this.mapFilter.isSynchronized)
        this.syncListWithMap();
      else
        this.usersLocationsList = this.concatWithMapRecords(dataResult.usersLocationsForList.data);
      this.loaded = true;
    }
    else {
      //invalidate the search as it's not the last one, do not wait for the result
      this.api.invalidateQueryCache(dataResult.searchUniqueId);
    }
  }

  public async fetchFromSearch(skip: number, take: number) {
    const dataResult = await this.api.loadFromUsersLatestLocationQuery(this.searchUniqueId, false, skip, take, this.queryMemory.sortExpression);
    return this.concatWithMapRecords(dataResult.usersLocationsForList.data);
  }

  private async sortExpressionChanged() {
    this.taskCounter.execute(() => tryExecute(() => this.reloadFromSearch(), E.SORT_ERROR_MESSAGE, E.SORT_ERROR_TITLE, true));
  }

  private async reloadFromSearch() {
    const queryId = ++this.lastQueryId;
    const dataResult = await this.api.loadFromUsersLatestLocationQuery(this.searchUniqueId, true, 0, this.batchSize, this.queryMemory.sortExpression);
    if (queryId === this.lastQueryId) {
      this.clearLastDetailUserLocation();
      dataResult.usersLocationsForMap.forEach(mapRecord => {
        const currentMapRecord = this.usersLocationsMap.find(x => x.location.id === mapRecord.location.id);
        currentMapRecord.rowIndex = mapRecord.rowIndex;
      })
      if (!this.mapFilter.isSynchronized) {
        this.usersLocationsList = this.concatWithMapRecords(dataResult.usersLocationsForList.data);
        this.unselectAll();
        this.recalculateIsSelected();
      }
      else
        this.usersLocationsList.sort((a, b) => a.rowIndex - b.rowIndex);
    }
  }

  public async getMapZone(): Promise<IMapZoneDetailDTO> {
    return this.queryMemory.filter.mapZone != null ? await this.mapZoneApi.getById(this.queryMemory.filter.mapZone.id) : null;
  }

  /* Map functions */

  public async mapFilterMutated() {
    if (this.previousMapFilter.isSynchronized !== this.mapFilter.isSynchronized) {
      if (this.mapFilter.isSynchronized) {
        this.syncListWithMap();
      }
      else {
        this.usersLocationsList = await this.fetchFromSearch(0, BATCH_SIZE);
      }
      this.clearLastDetailUserLocation();
      this.unselectAll();
      this.recalculateIsSelected();
      this.previousMapFilter.isSynchronized = this.mapFilter.isSynchronized
    }
    if (this.previousMapFilter.areSitesDisplayed !== this.mapFilter.areSitesDisplayed) {
      this.changeSitesVisibility(this.mapFilter.areSitesDisplayed);
      this.previousMapFilter.areSitesDisplayed = this.mapFilter.areSitesDisplayed;
    }
  }

  public mapMoved() {
    if (this.mapFilter.isSynchronized) {
      this.syncListWithMap();
      this.recalculateIsSelected()
    }
  }

  public mapClicked(latlng: L.LatLng) {
    if(this.isLocationSelecting) {
      this.queryMemory.filter.site = null;
      this.queryMemory.filter.selectedLocation = latlng;
    }
  }

  /* List functions */

  public listFilterMutated() {
    if (this.queryMemory.filter.distanceFromSiteInMeters < 1)
      this.queryMemory.filter.distanceFromSiteInMeters = 1;
    let redrawCircle = false;
    if (this.currentSite != this.queryMemory.filter.site) {
      this.currentSite = this.queryMemory.filter.site;
      redrawCircle = true;
    }
    if(this.currentSelectedLocation != this.queryMemory.filter.selectedLocation) {
      this.currentSelectedLocation = this.queryMemory.filter.selectedLocation;
      redrawCircle = true;
    }
    if (this.currentDistanceFromSite != this.queryMemory.filter.distanceFromSiteInMeters) {
      this.currentDistanceFromSite = this.queryMemory.filter.distanceFromSiteInMeters;
      redrawCircle = true;
    }
    if (redrawCircle)
      this.showSiteOrLocationInMap(this.currentSite, this.currentSelectedLocation, this.currentDistanceFromSite);
  }

  public enableLocationSelecting() {
    this.isLocationSelecting = true;
    document.addEventListener('click', () => this.disableLocationSelecting(), { once: true });
    this.toggleLocationSelectingInMap(this.isLocationSelecting);
  }

  public disableLocationSelecting() {
    this.isLocationSelecting = false;
    this.toggleLocationSelectingInMap(this.isLocationSelecting);
  }

  /* User location functions */

  public detailDisplayedInList(userLocation: UserLocation) {
    this.detailUserLocation(userLocation);
    this.showUserLocationDetailInMap(userLocation);
  }

  public async detailDisplayedInMap(userLocation: UserLocation): Promise<void> {
    if (!this.mapFilter.isSynchronized)
      await this.taskCounter.execute(() => this.ensureListRecords(userLocation));
    this.detailUserLocation(userLocation);
    this.taskQueue.queueMicroTask(() => this.showUserLocationDetailInList(userLocation));
  }

  public async selectAll(isSelected: boolean) {
    if (!this.mapFilter.isSynchronized && isSelected)
      await this.taskCounter.execute(() => this.ensureAllListRecords());
    this.usersLocationsList.forEach(userLocation => userLocation.isSelected = isSelected);
    this.recalculateIsSelected();
  }

  public recalculateIsSelected() {
    this.isSelectedAny = this.usersLocationsList.some(x => x.isSelected);
    this.isSelectedAll = this.usersLocationsList.length > 0 && this.usersLocationsList.every(x => x.isSelected);
  }

  public async requestUsersLocations(): Promise<UserLocationRequestDTO[]> {
    const results = await this.taskCounter.execute(() => this.api.requestUsersLocations({ searchUniqueId: this.searchUniqueId, usersUniqueIds: this.getSelectedUsersLocations().map(x => x.user.uniqueId) } as UsersLocationsRequestModel));
    results.forEach(userLocationRequestResult => {
      const userLocation = this.usersLocationsList.find(x => x.user.uniqueId == userLocationRequestResult.userUniqueId);
      userLocation.userLocationRequest = userLocationRequestResult;
    });
    return results;
  }

  public async sendUsersEmail(email: UsersEmail, recipients: MailAddressModel[]): Promise<void> {
    return await this.api.sendUserLocationEmails({ from: email.from, subject: email.subject, htmlBody: email.message, recipients: recipients } as UserLocationEmailsModel);
  }

  /* Helper functions */

  @computedFrom('usersLocationsList.length', 'mapFilter.isSynchronized', 'loaded')
  public get listHasMoreRecords(): boolean {
    return !this.mapFilter.isSynchronized && this.loaded && this.usersLocationsList.length < this.searchTotalRowCount;
  }

  public getSelectedUsersLocations(): UserLocation[] {
    return this.usersLocationsList.filter(x => x.isSelected);
  }

  private syncListWithMap() {
    const mapRecordsInView = this.getUsersLocationsInView();
    const listRecordsToRemove = this.usersLocationsList.filter(listRecord => listRecord.location == null || !mapRecordsInView.some(mapRecord => mapRecord.location.id === listRecord.location.id));
    listRecordsToRemove.forEach(x => this.usersLocationsList.splice(this.usersLocationsList.indexOf(x), 1));
    const listRecordsToAdd = mapRecordsInView.filter(mapRecord => !this.usersLocationsList.some(listRecord => listRecord.location.id === mapRecord.location.id));
    this.usersLocationsList.push(...listRecordsToAdd);
    this.usersLocationsList.sort((a, b) => a.rowIndex - b.rowIndex);
    this.getUsersLocationsOutsideView().forEach(x => x.isSelected = false);
  }

  private async ensureListRecords(requestedUserLocation: UserLocation) {
    if (this.usersLocationsList[this.usersLocationsList.length - 1].rowIndex + 20 < requestedUserLocation.rowIndex) {
      const take = requestedUserLocation.rowIndex - this.usersLocationsList[this.usersLocationsList.length - 1].rowIndex + 20;
      const dataResult = await this.fetchFromSearch(this.usersLocationsList.length, take);
      this.usersLocationsList.push(...dataResult);
    }
  }

  private async ensureAllListRecords() {
    if (this.usersLocationsList.length < this.searchTotalRowCount) {
      const dataResult = await this.fetchFromSearch(this.usersLocationsList.length, this.searchTotalRowCount - this.usersLocationsList.length);
      dataResult.forEach(userLocation => this.usersLocationsList.push(userLocation));
    }
  }

  private wrapRecord(userLocationRecord: IUserLocationRecordDTO): UserLocation {
    return {
      ...userLocationRecord,
      isSelected: false,
      isHighlighted: false,
      isDetail: false
    } as UserLocation;
  }

  private concatWithMapRecords(usersLocationsForList: UserLocationRecordDTO[]): UserLocation[] {
    return usersLocationsForList.map(listRecord => {
      const mapRecord = this.usersLocationsMap.find(mapRecord => listRecord.location != null && listRecord.location.id === mapRecord.location.id);
      if (mapRecord != null)
        return mapRecord;
      else
        return this.wrapRecord(listRecord);
    });
  }

  private detailUserLocation(userLocation: UserLocation) {
    const allSelectedUsersLocations = this.getAllSelectedUsersLocations();
    if (allSelectedUsersLocations.length === 0) {
      userLocation.isSelected = true;
      this.recalculateIsSelected();
    }
    if (this.lastDetailUserLocation != null) {
      if (allSelectedUsersLocations.length === 1 && allSelectedUsersLocations[0] === this.lastDetailUserLocation) {
        this.lastDetailUserLocation.isSelected = false;
        userLocation.isSelected = true;
        this.recalculateIsSelected();
      }
      this.lastDetailUserLocation.isDetail = false;
    }
    this.lastDetailUserLocation = userLocation;
    userLocation.isDetail = true;
  }

  private getAllSelectedUsersLocations(): UserLocation[] {
    return this.usersLocationsList.filter(x => x.isSelected);
  }

  private clearLastDetailUserLocation() {
    if (this.lastDetailUserLocation != null) {
      this.lastDetailUserLocation.isDetail = false;
      this.lastDetailUserLocation = null;
    }
  }

  private unselectAll() {
    this.usersLocationsMap.forEach(x => x.isSelected = false);
    this.usersLocationsList.forEach(x => x.isSelected = false);
  }

  get batchSize() {
    return BATCH_SIZE;
  }
}
